import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Map, { Marker, Layer, NavigationControl } from 'react-map-gl/maplibre';
import { Box } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';

// Keyless basemap — OpenFreeMap positron: clean, near-white, minimal so the
// startup logos are the focal point.
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
// PSU Business Accelerator, 2130 SW 5th Avenue, Portland, OR
const CENTER = { longitude: -122.6816, latitude: 45.5077 };
const INITIAL_ZOOM = 14;
const INITIAL_PITCH = 60;
const SPACING = 34; // px between fanned markers that share a coordinate

// 3D extruded buildings — OpenMapTiles vector source ('openmaptiles'/'building').
const buildingsLayer = {
  id: '3d-buildings',
  source: 'openmaptiles',
  'source-layer': 'building',
  filter: ['!=', ['get', 'hide_3d'], true],
  type: 'fill-extrusion',
  minzoom: 13,
  paint: {
    'fill-extrusion-color': '#ffffff',
    'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 13, 0, 14.5, ['get', 'render_height']],
    'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 13, 0, 14.5, ['get', 'render_min_height']],
    'fill-extrusion-opacity': 0.45,
  },
};

const domainOf = (website) =>
  (website || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '').split(/[/?#]/)[0].trim();

const initialsOf = (name) =>
  name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// Logo-forward marker: clean white tile with the company logo as the hero.
// icon.horse resolves logos for niche domains; unavatar + DuckDuckGo back it up.
// (Google's favicon service is intentionally NOT used here — it returns a generic
// globe image with a 200 status, which defeats the onError fallback chain.)
const MarkerBadge = ({ startup, isActive }) => {
  const domain = domainOf(startup.website);
  const sources = [
    startup.logo_url,
    domain && `https://icon.horse/icon/${domain}`,
    domain && `https://unavatar.io/${domain}?fallback=false`,
    domain && `https://icons.duckduckgo.com/ip3/${domain}.ico`,
  ].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const size = isActive ? 46 : 38;
  const hasLogo = idx < sources.length;
  return (
    <div
      data-testid={`map-marker-${startup.id}`}
      style={{
        width: size, height: size, borderRadius: 13,
        background: '#ffffff',
        border: isActive ? '2px solid #0f172a' : '1px solid rgba(15,23,42,0.10)',
        boxShadow: isActive ? '0 10px 26px rgba(15,23,42,0.22)' : '0 2px 10px rgba(15,23,42,0.14)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', cursor: 'pointer',
        transition: 'width .15s ease, height .15s ease, box-shadow .15s ease',
      }}
    >
      {hasLogo ? (
        <img
          src={sources[idx]} alt="" referrerPolicy="no-referrer"
          onError={() => setIdx((i) => i + 1)}
          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
        />
      ) : (
        <span style={{ color: '#334155', fontWeight: 700, fontSize: isActive ? 15 : 13, letterSpacing: '-0.02em' }}>
          {initialsOf(startup.name)}
        </span>
      )}
    </div>
  );
};

const ClusterBubble = ({ cluster }) => {
  const size = Math.min(74, Math.max(46, 32 + cluster.count * 2.2));
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: '#ffffff', color: '#0f172a',
        border: '1px solid rgba(15,23,42,0.12)', boxShadow: '0 4px 16px rgba(15,23,42,0.14)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', letterSpacing: '-0.02em',
      }}
    >
      <div style={{ fontSize: size >= 58 ? 18 : 15, fontWeight: 700, lineHeight: 1 }}>{cluster.count}</div>
      <div style={{ fontSize: 9, opacity: 0.5, marginTop: 2, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        startups
      </div>
      <div style={{ marginTop: 3, height: 7 }}>
        {(cluster.sample || []).slice(0, 3).map((s, i) => (
          <span key={i} style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: s.color, margin: '0 1px', opacity: 0.8 }} />
        ))}
      </div>
    </div>
  );
};

const StartupMap = ({ startups, clusters, clusterMode, activeId, onSelect, onClusterZoom }) => {
  const mapRef = useRef(null);
  const [view, setView] = useState({ bounds: null, zoom: INITIAL_ZOOM, pitch: INITIAL_PITCH });
  const [beforeId, setBeforeId] = useState(undefined);
  const [mapError, setMapError] = useState(false);

  const handleError = useCallback((e) => {
    const msg = String(e?.error?.message || '').toLowerCase();
    // Only flag a hard basemap/style load failure, not transient tile hiccups.
    if (msg.includes('style') && (msg.includes('fetch') || msg.includes('load') || msg.includes('failed'))) {
      setMapError(true);
    }
  }, []);

  const syncView = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    const b = m.getBounds();
    setView({
      bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
      zoom: m.getZoom(),
      pitch: m.getPitch(),
    });
  }, []);

  const is3D = view.pitch >= 30;
  const togglePitch = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    m.easeTo({ pitch: m.getPitch() >= 30 ? 0 : INITIAL_PITCH, duration: 600 });
  }, []);

  const handleLoad = useCallback((e) => {
    const map = e.target;
    const firstSymbol = (map.getStyle().layers || []).find((l) => l.type === 'symbol');
    setBeforeId(firstSymbol ? firstSymbol.id : undefined);
    syncView();
  }, [syncView]);

  // Fly to the active startup when it changes.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !activeId) return;
    const a = startups.find((s) => s.id === activeId);
    if (a) m.flyTo({ center: [a.lng, a.lat], zoom: Math.max(m.getZoom(), 15), duration: 800 });
  }, [activeId, startups]);

  // Viewport-cull + fan out markers that share a coordinate.
  const positioned = useMemo(() => {
    if (clusterMode) return [];
    let visible = startups;
    const b = view.bounds;
    if (b) {
      const [w, s, e, n] = b;
      const padX = (e - w) * 0.3, padY = (n - s) * 0.3;
      visible = startups.filter(
        (st) => st.lng >= w - padX && st.lng <= e + padX && st.lat >= s - padY && st.lat <= n + padY
      );
      if (activeId && !visible.some((st) => st.id === activeId)) {
        const a = startups.find((st) => st.id === activeId);
        if (a) visible = [...visible, a];
      }
    }
    const groups = {};
    visible.forEach((st) => {
      const key = `${st.lat.toFixed(4)},${st.lng.toFixed(4)}`;
      (groups[key] = groups[key] || []).push(st);
    });
    const out = [];
    Object.values(groups).forEach((group) => {
      const nn = group.length;
      group.forEach((st, i) => {
        const off = nn === 1 ? 0 : (i - (nn - 1) / 2) * SPACING;
        out.push({ st, offset: [off, off] }); // 45° diagonal fan
      });
    });
    return out;
  }, [startups, view.bounds, activeId, clusterMode]);

  return (
    <div className="relative h-screen w-screen z-0" style={{ background: '#ffffff' }}>
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: CENTER.longitude, latitude: CENTER.latitude,
          zoom: INITIAL_ZOOM, pitch: INITIAL_PITCH, bearing: 0,
        }}
        mapStyle={MAP_STYLE}
        maxPitch={85}
        style={{ width: '100%', height: '100%' }}
        onLoad={handleLoad}
        onError={handleError}
        onMoveEnd={syncView}
        onZoomEnd={syncView}
        onPitchEnd={syncView}
      >
        <Layer beforeId={beforeId} {...buildingsLayer} />
        <NavigationControl position="top-right" showCompass visualizePitch />

        {clusterMode
          ? (clusters || []).map((c) => (
              <Marker
                key={c.neighborhood}
                longitude={c.lng}
                latitude={c.lat}
                anchor="center"
                onClick={(e) => {
                  if (e && e.originalEvent) e.originalEvent.stopPropagation();
                  mapRef.current?.flyTo({ center: [c.lng, c.lat], zoom: Math.max(view.zoom + 1, 15), duration: 700 });
                  if (onClusterZoom) onClusterZoom(c);
                }}
              >
                <ClusterBubble cluster={c} />
              </Marker>
            ))
          : positioned.map(({ st, offset }) => (
              <Marker
                key={st.id}
                longitude={st.lng}
                latitude={st.lat}
                offset={offset}
                anchor="center"
                onClick={(e) => {
                  if (e && e.originalEvent) e.originalEvent.stopPropagation();
                  onSelect(st.id);
                }}
              >
                <MarkerBadge startup={st} isActive={activeId === st.id} />
              </Marker>
            ))}
      </Map>

      {mapError && (
        <div
          data-testid="map-error-banner"
          style={{ position: 'absolute', top: 74, left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}
          className="max-w-[92%] sm:max-w-md rounded-xl bg-amber-50 border border-amber-300 text-amber-900 shadow-lg px-4 py-2.5 text-[12.5px] leading-snug text-center"
        >
          <span className="font-semibold">Map imagery is temporarily unavailable.</span>{' '}
          Startup pins, search and details still work below.
        </div>
      )}

      <button
        onClick={togglePitch}
        data-testid="map-3d-toggle"
        title={is3D ? 'Switch to flat 2D view' : 'Switch to tilted 3D view'}
        style={{ position: 'absolute', top: 250, right: 6, zIndex: 5 }}
        className="inline-flex items-center gap-1.5 rounded-lg bg-white text-slate-800 border border-slate-200 shadow-md px-2.5 py-1.5 text-[12px] font-semibold hover:bg-slate-50 transition-colors"
      >
        <Box size={14} className={is3D ? 'text-emerald-600' : 'text-slate-500'} />
        {is3D ? '2D' : '3D'}
      </button>
    </div>
  );
};

export default StartupMap;
